import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { CheckCircle2, XCircle, Loader2, MessageSquareX, RefreshCw } from 'lucide-react';

/**
 * HR-facing queue of attendance regularization requests.
 * Uses:
 *   GET  /api/attendance/regularizations?status=…
 *   POST /api/attendance/regularizations/{id}/approve
 *   POST /api/attendance/regularizations/{id}/reject
 */
export const RegularizationRequestsTab = () => {
  const [status, setStatus] = useState('pending');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectNote, setRejectNote] = useState('');

  const fetchRows = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/attendance/regularizations?status=${status}`);
      setRows(res.data?.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load requests');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchRows(); /* eslint-disable-next-line */ }, [status]);

  const handleApprove = async (row) => {
    setBusyId(row.id);
    try {
      const res = await api.post(`/attendance/regularizations/${row.id}/approve`, {});
      if (res.data?.success) {
        toast.success(`Approved — ${row.employee_name} for ${row.date}`);
        fetchRows();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Approval failed');
    } finally { setBusyId(null); }
  };

  const openReject = (row) => { setRejectFor(row); setRejectNote(''); };

  const submitReject = async () => {
    if (!rejectNote.trim()) { toast.error('Please add a reason for rejection'); return; }
    setBusyId(rejectFor.id);
    try {
      const res = await api.post(
        `/attendance/regularizations/${rejectFor.id}/reject`,
        { note: rejectNote.trim() }
      );
      if (res.data?.success) {
        toast.success(`Rejected — ${rejectFor.employee_name} for ${rejectFor.date}`);
        setRejectFor(null);
        fetchRows();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Rejection failed');
    } finally { setBusyId(null); }
  };

  const statusBadge = (s) => {
    if (s === 'pending')  return <Badge className="bg-amber-500/10 text-amber-500">Pending</Badge>;
    if (s === 'approved') return <Badge className="bg-emerald-500/10 text-emerald-500">Approved</Badge>;
    if (s === 'rejected') return <Badge className="bg-red-500/10 text-red-500">Rejected</Badge>;
    return <Badge variant="outline">{s}</Badge>;
  };

  return (
    <div className="space-y-4" data-testid="regularization-tab">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-[160px]" data-testid="reg-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchRows} data-testid="reg-refresh">
            <RefreshCw className="h-4 w-4 mr-1" />Refresh
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Approving writes the attendance row + emails the employee. Rejection requires a note.
        </p>
      </div>

      <Card><CardContent className="p-0">
        {loading ? (
          <div className="flex items-center justify-center h-48"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 text-muted-foreground">
            <MessageSquareX className="h-10 w-10 mb-3 opacity-50" />
            <p className="text-sm">No {status === 'all' ? '' : status} regularization requests</p>
          </div>
        ) : (
          <Table data-testid="regularization-table">
            <TableHeader>
              <TableRow>
                <TableHead>Employee</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Requested Times</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>Submitted</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map(r => (
                <TableRow key={r.id} data-testid={`reg-row-${r.id}`}>
                  <TableCell>
                    <div className="font-medium">{r.employee_name}</div>
                    <div className="text-[10px] text-muted-foreground">{r.employee_email}</div>
                  </TableCell>
                  <TableCell className="font-mono text-sm">{r.date}</TableCell>
                  <TableCell className="font-mono text-xs">
                    {r.requested_check_in || '—'} → {r.requested_check_out || '—'}
                    {r.projected_hours != null && (
                      <span className="ml-1 text-muted-foreground">({r.projected_hours}h)</span>
                    )}
                  </TableCell>
                  <TableCell className="max-w-[280px] text-sm">
                    <div className="truncate" title={r.reason}>{r.reason}</div>
                    {r.reviewer_note && (
                      <div className="text-[10px] text-muted-foreground truncate" title={r.reviewer_note}>
                        Reviewer: {r.reviewer_note}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {(r.created_at || '').split('T')[0]}
                  </TableCell>
                  <TableCell>{statusBadge(r.status)}</TableCell>
                  <TableCell className="text-right">
                    {r.status === 'pending' ? (
                      <div className="flex items-center justify-end gap-1">
                        <Button size="sm" variant="outline" className="h-7 text-xs"
                          onClick={() => handleApprove(r)} disabled={busyId === r.id}
                          data-testid={`reg-approve-${r.id}`}>
                          {busyId === r.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <><CheckCircle2 className="h-3 w-3 mr-1" />Approve</>}
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 text-xs"
                          onClick={() => openReject(r)} disabled={busyId === r.id}
                          data-testid={`reg-reject-${r.id}`}>
                          <XCircle className="h-3 w-3 mr-1" />Reject
                        </Button>
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent></Card>

      <Dialog open={!!rejectFor} onOpenChange={(o) => !o && setRejectFor(null)}>
        <DialogContent className="max-w-md" data-testid="reg-reject-dialog">
          <DialogHeader>
            <DialogTitle>Reject regularization</DialogTitle>
            <DialogDescription>
              This will notify <strong>{rejectFor?.employee_name}</strong> that
              their request for <strong>{rejectFor?.date}</strong> was rejected.
              A reason is required so they know what to do next.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            rows={4} maxLength={500}
            placeholder="e.g. Please raise a leave request instead — this was a planned absence"
            value={rejectNote} onChange={(e) => setRejectNote(e.target.value)}
            data-testid="reg-reject-note"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectFor(null)} data-testid="reg-reject-cancel">Cancel</Button>
            <Button onClick={submitReject} disabled={busyId === rejectFor?.id} data-testid="reg-reject-submit">
              {busyId === rejectFor?.id && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Reject request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RegularizationRequestsTab;
